import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission, PERMISSIONS } from '../utils/rolePermissions';
import { countCandidates } from '../services/firestoreService';
import { SkeletonStatCard, SkeletonListItem } from '../components/ui/Skeleton';
import '../styles/Dashboard.css';

const Dashboard = () => {
  // districts available from shared context — no extra fetch needed
  const { currentUser, districts } = useAuth();
  const [stats, setStats] = useState({
    totalCandidates: 0,
    supplyPastors: 0,
    localPastors: 0,
    clms: 0,
    cmcs: 0,
    layMembers: 0,
    pendingReports: 0
  });
  const [recentCandidates, setRecentCandidates] = useState([]);
  const [loading, setLoading] = useState(true);

  const userDistrictIds = currentUser?.districtIds || (currentUser?.districtId ? [currentUser.districtId] : []);
  const userRole = currentUser?.role;
  const isDCOM = userRole === 'dCOM' || userRole === 'dCOM Member' || userRole === 'dCOM Chair';

  // Resolve district names once districts are available from context
  const userDistrictNames = isDCOM && districts.length > 0
    ? userDistrictIds.map(id => districts.find(d => d.id === id)?.name).filter(Boolean)
    : [];

  useEffect(() => {
    // Wait until we have district data for dCOM users before fetching stats
    if (!isDCOM || districts.length > 0) {
      fetchDashboardStats();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, districts]);

  const fetchDashboardStats = async () => {
    try {
      const candidatesRef = collection(db, 'candidates');

      // Use server-side count() aggregations instead of loading all candidates
      const districtFilter = isDCOM ? userDistrictNames : undefined;

      // Run all status counts in parallel
      const [
        totalCount,
        supplyCount,
        localCount,
        clmCount,
        cmcCount,
        layCount,
      ] = await Promise.all([
        countCandidates({ districtNames: districtFilter }),
        countCandidates({ districtNames: districtFilter, status: 'supply-pastor' }),
        countCandidates({ districtNames: districtFilter, status: 'local-pastor' }),
        countCandidates({ districtNames: districtFilter, status: 'clm' }),
        countCandidates({ districtNames: districtFilter, status: 'cmc' }),
        countCandidates({ districtNames: districtFilter, status: 'lay' }),
      ]);

      // Get 5 most recently updated candidates (district-filtered for dCOM users)
      try {
        const recentConstraints = [orderBy('updatedAt', 'desc'), limit(isDCOM ? 20 : 5)];
        if (isDCOM && userDistrictNames.length > 0) {
          recentConstraints.unshift(where('district', 'in', userDistrictNames));
        }
        const recentSnapshot = await getDocs(query(candidatesRef, ...recentConstraints));
        setRecentCandidates(
          recentSnapshot.docs.map(d => ({ id: d.id, ...d.data() })).slice(0, 5)
        );
      } catch {
        // Fallback to createdAt if updatedAt index missing
        try {
          const recentConstraints = [orderBy('createdAt', 'desc'), limit(isDCOM ? 20 : 5)];
          if (isDCOM && userDistrictNames.length > 0) {
            recentConstraints.unshift(where('district', 'in', userDistrictNames));
          }
          const recentSnapshot = await getDocs(query(candidatesRef, ...recentConstraints));
          setRecentCandidates(
            recentSnapshot.docs.map(d => ({ id: d.id, ...d.data() })).slice(0, 5)
          );
        } catch {
          setRecentCandidates([]);
        }
      }

      // Pending action reports count
      const pendingSnapshot = await getDocs(
        query(collection(db, 'actionReports'), where('status', '==', 'pending'))
      );

      setStats({
        totalCandidates: totalCount,
        supplyPastors: supplyCount,
        localPastors: localCount,
        clms: clmCount,
        cmcs: cmcCount,
        layMembers: layCount,
        pendingReports: pendingSnapshot.size,
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1>Ministry CRM Dashboard</h1>
          <p>Board of Ordained Ministry &amp; District Committee on Ordained Ministry</p>
        </div>
        <div className="stats-grid">
          {Array.from({ length: 7 }).map((_, i) => <SkeletonStatCard key={i} />)}
        </div>
        <div className="recent-activity" style={{ marginTop: '2rem' }}>
          <h2>Recent Activity</h2>
          {Array.from({ length: 5 }).map((_, i) => <SkeletonListItem key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Ministry CRM Dashboard</h1>
        <p>Board of Ordained Ministry & District Committee on Ordained Ministry</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Candidates</h3>
          <div className="stat-number">{stats.totalCandidates}</div>
          <Link to="/candidates" className="stat-link">View All →</Link>
        </div>

        <div className="stat-card">
          <h3>Candidates in Process</h3>
          <div className="stat-number">{stats.layMembers}</div>
          <Link to="/candidates?status=lay" className="stat-link">View Details →</Link>
        </div>

        <div className="stat-card">
          <h3>CLMs</h3>
          <div className="stat-number">{stats.clms}</div>
          <Link to="/candidates?status=clm" className="stat-link">View Details →</Link>
        </div>

        <div className="stat-card">
          <h3>Certified Candidates</h3>
          <div className="stat-number">{stats.cmcs}</div>
          <Link to="/candidates?status=cmc" className="stat-link">View Details →</Link>
        </div>

        <div className="stat-card">
          <h3>Supply Pastors</h3>
          <div className="stat-number">{stats.supplyPastors}</div>
          <Link to="/candidates?status=supply-pastor" className="stat-link">View Details →</Link>
        </div>

        <div className="stat-card">
          <h3>Local Pastors</h3>
          <div className="stat-number">{stats.localPastors}</div>
          <Link to="/candidates?status=local-pastor" className="stat-link">View Details →</Link>
        </div>

        <div className="stat-card">
          <h3>Pending Reports</h3>
          <div className="stat-number">{stats.pendingReports}</div>
          <Link to="/action-reports" className="stat-link">Review Reports →</Link>
        </div>
      </div>

      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="action-buttons">
          <Link to="/candidates/add" className="action-btn primary">
            Add New Candidate
          </Link>
          <Link to="/course-of-study" className="action-btn">
            Course of Study Tracker
          </Link>
          <Link to="/action-reports" className="action-btn">
            Action Reports
          </Link>
        </div>
      </div>

      <div className="recent-activity">
        <h2>Recent Activity</h2>
        {recentCandidates.length > 0 ? (
          <div className="recent-candidates">
            <h3>Recently Updated Candidates</h3>
            <div className="recent-list">
              {recentCandidates.map(candidate => (
                <div key={candidate.id} className="recent-item">
                  <div className="recent-info">
                    <Link to={`/candidates/${candidate.id}`} className="recent-name">
                      {candidate.firstName} {candidate.lastName}
                    </Link>
                    <div className="recent-details">
                      <span className="recent-status">
                        {candidate.conferenceStatus === 'lay' ? 'Lay' :
                         candidate.conferenceStatus === 'clm' ? 'CLM' :
                         candidate.conferenceStatus === 'cmc' ? 'CMC' :
                         candidate.conferenceStatus === 'supply-pastor' ? 'Supply Pastor' :
                         candidate.conferenceStatus === 'local-pastor' ? 'Local Pastor' :
                         candidate.conferenceStatus === 'retired-local' ? 'Retired Local Pastor' :

                         candidate.conferenceStatus || 'Unknown'}
                      </span>
                      {candidate.district && (
                        <span className="recent-district"> • {candidate.district}</span>
                      )}
                    </div>
                  </div>
                  <div className="recent-date">
                    {candidate.updatedAt ? 
                      new Date(candidate.updatedAt.toDate()).toLocaleDateString() :
                      candidate.createdAt ? 
                        new Date(candidate.createdAt.toDate()).toLocaleDateString() :
                        'No date'
                    }
                  </div>
                </div>
              ))}
            </div>
            <Link to="/candidates" className="view-all-link">View All Candidates →</Link>
          </div>
        ) : (
          <div className="activity-placeholder">
            <p>No recent candidate activity to display.</p>
            <Link to="/candidates/add" className="add-first-candidate">Add your first candidate</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
